import { fetchAuthSession } from 'aws-amplify/auth';
import { createContext, PropsWithChildren, useContext, useState, useEffect, useRef } from 'react';

import type { ChatAttachment } from '@/models/GroupChatMessage';
import type { Mention } from '@/models/Mention';

import { useAuthContext } from '@/context/AuthProvider';
import { useLogger } from '@/hooks/useLogger';
import { ChatMessage } from '@/models/ChatMessage';
import { AiProgressMessage } from '@/models/DocumentProgressMessage';
import { getAccessToken, setAccessToken } from '@/utils/authTokens';

// Group chat send payload types
type GroupSendMessage = {
  action: 'sendMessage';
  propertyId: string;
  content: string;
  clientMessageId?: string;
  mentions?: Mention[];
};
type GroupDeleteMessage = {
  action: 'deleteMessage';
  propertyId: string;
  createdAt: number;
};
type GroupEditMessage = {
  action: 'editMessage';
  propertyId: string;
  createdAt: number;
  content: string;
};
type GroupReactMessage = {
  action: 'reactToMessage';
  propertyId: string;
  createdAt: number;
  emoji: string;
  reactionAction: 'add' | 'remove';
};

// Client group chat send payload types
type ClientGroupSendMessage = {
  action: 'sendGroupMessage';
  groupId: string;
  content: string;
  clientMessageId?: string;
  mentions?: Mention[];
  attachments?: ChatAttachment[];
};
type ClientGroupDeleteMessage = {
  action: 'deleteGroupMessage';
  groupId: string;
  createdAt: number;
};
type ClientGroupEditMessage = {
  action: 'editGroupMessage';
  groupId: string;
  createdAt: number;
  content: string;
};
type ClientGroupReactMessage = {
  action: 'reactToGroupMessage';
  groupId: string;
  createdAt: number;
  emoji: string;
  reactionAction: 'add' | 'remove';
};

export type WebSocketSendPayload =
  | GroupSendMessage
  | GroupDeleteMessage
  | GroupEditMessage
  | GroupReactMessage
  | ClientGroupSendMessage
  | ClientGroupDeleteMessage
  | ClientGroupEditMessage
  | ClientGroupReactMessage;

interface ChatWebSocketMessage {
  type: 'NEW_MESSAGE';
  propertyId: string;
  data: ChatMessage;
}

interface DeletedMessageWebSocketMessage {
  type: 'MESSAGE_DELETED';
  propertyId: string;
  data: { propertyId: string; createdAt: number; messageId: string };
}

interface EditedMessageWebSocketMessage {
  type: 'MESSAGE_EDITED';
  propertyId: string;
  data: {
    propertyId: string;
    createdAt: number;
    messageId: string;
    content: string;
    editedAt: number;
  };
}

interface ReactionMessageWebSocketMessage {
  type: 'MESSAGE_REACTION';
  propertyId: string;
  data: {
    propertyId: string;
    createdAt: number;
    userId: string;
    emoji: string;
    userName: string;
    action: 'add' | 'remove';
  };
}

interface GroupChatWebSocketMessage {
  type: 'NEW_GROUP_MESSAGE';
  groupId: string;
  data: import('@/models/GroupChatMessage').GroupChatMessage;
}

interface GroupDeletedMessageWebSocketMessage {
  type: 'GROUP_MESSAGE_DELETED';
  groupId: string;
  data: { groupId: string; createdAt: number; messageId: string };
}

interface GroupEditedMessageWebSocketMessage {
  type: 'GROUP_MESSAGE_EDITED';
  groupId: string;
  data: {
    groupId: string;
    createdAt: number;
    messageId: string;
    content: string;
    editedAt: number;
  };
}

interface GroupReactionMessageWebSocketMessage {
  type: 'GROUP_MESSAGE_REACTION';
  groupId: string;
  data: {
    groupId: string;
    createdAt: number;
    userId: string;
    emoji: string;
    userName: string;
    action: 'add' | 'remove';
  };
}

export interface AskHauserChunkWebSocketMessage {
  type: 'ASK_HAUSER_CHUNK';
  messageId: string;
  chunk: string;
  index: number;
}

export interface AskHauserCompleteWebSocketMessage {
  type: 'ASK_HAUSER_COMPLETE';
  messageId: string;
  inputTokens: number;
  outputTokens: number;
}

export interface AskHauserToolResultWebSocketMessage {
  type: 'ASK_HAUSER_TOOL_RESULT';
  messageId: string;
  toolName: string;
  toolInput: Record<string, unknown>;
  result: unknown;
  index: number;
}

interface WebSocketProviderProps {
  aiProgressMessage?: AiProgressMessage;
  lastChatMessage?: ChatWebSocketMessage;
  lastDeletedMessage?: DeletedMessageWebSocketMessage;
  lastEditedMessage?: EditedMessageWebSocketMessage;
  lastReactionMessage?: ReactionMessageWebSocketMessage;
  lastGroupChatMessage?: GroupChatWebSocketMessage;
  lastGroupDeletedMessage?: GroupDeletedMessageWebSocketMessage;
  lastGroupEditedMessage?: GroupEditedMessageWebSocketMessage;
  lastGroupReactionMessage?: GroupReactionMessageWebSocketMessage;
  lastAskHauserChunk?: AskHauserChunkWebSocketMessage;
  lastAskHauserComplete?: AskHauserCompleteWebSocketMessage;
  lastAskHauserToolResult?: AskHauserToolResultWebSocketMessage;
  sendWebSocketMessage: (data: WebSocketSendPayload) => boolean;
  /** Increments on every successful connection, including reconnects. Use to trigger post-reconnect state sync. */
  connectionAttempt: number;
}

export const WebSocketContext = createContext<WebSocketProviderProps | null>(null);

export const useWebSocketContext = () => {
  const context = useContext(WebSocketContext);

  if (!context)
    throw new Error('useWebSocketContext context must be used inside WebSocketProvider');

  return context;
};

const webSocketUrl = import.meta.env.VITE_WEBSOCKET_URL as string;

// Heartbeat interval in ms — just under API Gateway's 10-minute idle timeout.
const HEARTBEAT_INTERVAL_MS = 9 * 60 * 1000;

export const WebSocketProvider = ({ children }: PropsWithChildren) => {
  const socketRef = useRef<WebSocket | null>(null);
  const isIntentionalCloseRef = useRef(false);
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Bumped whenever the connection owner (effect run) is torn down, so an async
  // reconnect that resolves after unmount / user change does not open a socket.
  const connectionGenerationRef = useRef(0);
  const { user } = useAuthContext();
  const logger = useLogger('WebSocketProvider');

  const [connectionAttempt, setConnectionAttempt] = useState(0);
  const [aiProgressMessage, setAiProgressMessage] = useState<AiProgressMessage>();
  const [lastChatMessage, setLastChatMessage] = useState<ChatWebSocketMessage>();
  const [lastDeletedMessage, setLastDeletedMessage] = useState<DeletedMessageWebSocketMessage>();
  const [lastEditedMessage, setLastEditedMessage] = useState<EditedMessageWebSocketMessage>();
  const [lastReactionMessage, setLastReactionMessage] = useState<ReactionMessageWebSocketMessage>();
  const [lastGroupChatMessage, setLastGroupChatMessage] = useState<GroupChatWebSocketMessage>();
  const [lastGroupDeletedMessage, setLastGroupDeletedMessage] =
    useState<GroupDeletedMessageWebSocketMessage>();
  const [lastGroupEditedMessage, setLastGroupEditedMessage] =
    useState<GroupEditedMessageWebSocketMessage>();
  const [lastGroupReactionMessage, setLastGroupReactionMessage] =
    useState<GroupReactionMessageWebSocketMessage>();
  const [lastAskHauserChunk, setLastAskHauserChunk] = useState<AskHauserChunkWebSocketMessage>();
  const [lastAskHauserComplete, setLastAskHauserComplete] =
    useState<AskHauserCompleteWebSocketMessage>();
  const [lastAskHauserToolResult, setLastAskHauserToolResult] =
    useState<AskHauserToolResultWebSocketMessage>();

  const userId = user?.id;

  const clearHeartbeat = () => {
    if (heartbeatRef.current !== null) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  };

  const clearReconnectTimeout = () => {
    if (reconnectTimeoutRef.current !== null) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  };

  // Amplify refreshes an expired access token here (without forcing a refresh),
  // so a reconnect after the 30-minute token lifetime still authenticates.
  const getFreshAccessToken = async (): Promise<string | null> => {
    try {
      const { tokens } = await fetchAuthSession();
      const accessToken = tokens?.accessToken?.toString();
      if (accessToken) {
        setAccessToken(accessToken);
        return accessToken;
      }
    } catch (error) {
      logger.warn('WebSocketProvider, could not refresh the access token', { error });
    }
    return getAccessToken();
  };

  const openWebSocketConnection = (accessToken: string | null) => {
    if (!accessToken) {
      logger.warn('WebSocketProvider, no access token, not opening web socket connection');
      return;
    }

    logger.debug(`WebSocketProvider, opening web socket connection, url: ${webSocketUrl}`);
    // Browsers cannot set headers on a WebSocket handshake, so the Cognito access
    // token goes in the query string; the $connect handler verifies it and takes
    // the userId from its `sub` claim.
    socketRef.current = new WebSocket(
      `${webSocketUrl}?token=${encodeURIComponent(accessToken)}`,
    );

    socketRef.current.onopen = () => {
      logger.debug('connected to WebSocket');
      setConnectionAttempt((n) => n + 1);

      // Heartbeat: send a ping just under API Gateway's 10-minute idle timeout so
      // the connection isn't silently killed during long idle periods.
      clearHeartbeat();
      heartbeatRef.current = setInterval(() => {
        if (socketRef.current?.readyState === WebSocket.OPEN) {
          socketRef.current.send(JSON.stringify({ action: 'ping' }));
        }
      }, HEARTBEAT_INTERVAL_MS);
    };

    socketRef.current.onmessage = (event) => {
      let message: Record<string, unknown>;
      try {
        message = JSON.parse(event.data) as Record<string, unknown>;
      } catch {
        logger.warn('WebSocket received non-JSON message, ignoring:', event.data);
        return;
      }
      logger.debug('received from WebSocket, message:', message);

      switch (message.type) {
        case 'NEW_MESSAGE':
          setLastChatMessage(message as unknown as ChatWebSocketMessage);
          break;
        case 'MESSAGE_DELETED':
          setLastDeletedMessage(message as unknown as DeletedMessageWebSocketMessage);
          break;
        case 'MESSAGE_EDITED':
          setLastEditedMessage(message as unknown as EditedMessageWebSocketMessage);
          break;
        case 'MESSAGE_REACTION':
          setLastReactionMessage(message as unknown as ReactionMessageWebSocketMessage);
          break;
        case 'NEW_GROUP_MESSAGE':
          setLastGroupChatMessage(message as unknown as GroupChatWebSocketMessage);
          break;
        case 'GROUP_MESSAGE_DELETED':
          setLastGroupDeletedMessage(message as unknown as GroupDeletedMessageWebSocketMessage);
          break;
        case 'GROUP_MESSAGE_EDITED':
          setLastGroupEditedMessage(message as unknown as GroupEditedMessageWebSocketMessage);
          break;
        case 'GROUP_MESSAGE_REACTION':
          setLastGroupReactionMessage(message as unknown as GroupReactionMessageWebSocketMessage);
          break;
        case 'ASK_HAUSER_CHUNK':
          setLastAskHauserChunk(message as unknown as AskHauserChunkWebSocketMessage);
          break;
        case 'ASK_HAUSER_COMPLETE':
          setLastAskHauserComplete(message as unknown as AskHauserCompleteWebSocketMessage);
          break;
        case 'ASK_HAUSER_TOOL_RESULT':
          setLastAskHauserToolResult(message as unknown as AskHauserToolResultWebSocketMessage);
          break;
        default:
          // Treat as AI progress message (existing behavior)
          setAiProgressMessage(message as unknown as AiProgressMessage);
          break;
      }
    };

    socketRef.current.onerror = (err) => {
      logger.error('WebSocket error:', err);
    };

    socketRef.current.onclose = () => {
      clearHeartbeat();
      clearReconnectTimeout();

      if (isIntentionalCloseRef.current) {
        isIntentionalCloseRef.current = false;
        logger.debug('WebSocket closed intentionally, not reconnecting');
        return;
      }

      logger.debug('WebSocket closed unexpectedly, reconnecting in 2 seconds...');
      const generation = connectionGenerationRef.current;
      reconnectTimeoutRef.current = setTimeout(() => {
        reconnectTimeoutRef.current = null;
        getFreshAccessToken().then((accessToken) => {
          if (generation !== connectionGenerationRef.current) return;
          openWebSocketConnection(accessToken);
        });
      }, 2000);
    };
  };

  const closeWebSocketConnection = () => {
    logger.debug(`WebSocketProvider, closing WebSocket connection`);
    const socket = socketRef.current;
    clearHeartbeat();
    clearReconnectTimeout();

    if (!socket || socket.readyState === WebSocket.CLOSED) {
      // No open socket — nothing to close, don't set the intentional flag.
      return;
    }

    isIntentionalCloseRef.current = true;
    socket.close();
  };

  const sendWebSocketMessage = (data: WebSocketSendPayload): boolean => {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify(data));
      return true;
    }
    logger.warn('WebSocket is not open, cannot send message:', data);
    return false;
  };

  useEffect(() => {
    logger.debug(`initializing WebSocketProvider`);
  }, []);

  useEffect(() => {
    if (!userId) {
      return () => {
        connectionGenerationRef.current += 1;
        closeWebSocketConnection();
      };
    }

    // AuthProvider stores a freshly fetched access token before it sets the user.
    openWebSocketConnection(getAccessToken());

    return () => {
      connectionGenerationRef.current += 1;
      closeWebSocketConnection();
    };
  }, [userId]);

  return (
    <WebSocketContext.Provider
      value={{
        aiProgressMessage,
        lastChatMessage,
        lastDeletedMessage,
        lastEditedMessage,
        lastReactionMessage,
        lastGroupChatMessage,
        lastGroupDeletedMessage,
        lastGroupEditedMessage,
        lastGroupReactionMessage,
        lastAskHauserChunk,
        lastAskHauserComplete,
        lastAskHauserToolResult,
        sendWebSocketMessage,
        connectionAttempt,
      }}>
      {children}
    </WebSocketContext.Provider>
  );
};
